import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
//---- Automatically generated, don't update this part ----

export default class DpCycleDeclaredJournalEvent extends JournalEvent {
  static type = "dp-cycle-declared-journal-event";

  /**
   * The weight in kg (2 decimals)
   */
  @attr("number") declare weight: number;

  /**
   * The pocket volume in L (2 decimals)
   */
  @attr("number") declare pocketVolume: number;

  /**
   * The systolic pressure in mmHg
   */
  @attr("number") declare systolicPressure: number;

  /**
   * The diastolic pressure in mmHg
   */
  @attr("number") declare diastolicPressure: number;

  /**
   * The heart rate in bpm
   */
  @attr("number") declare heartRate: number;

  /**
   * The ultrafiltration rate in %
   */
  @attr("number") declare ultrafiltrationRate: number;

  /**
   * The qualitative comment on the appearance of peritoneal fluid
   */
  @attr("string") declare peritonealFluidAppearance: string;

  /**
   * The urine output in ml/24h
   */
  @attr("number") declare urineOutput: number;

  /**
   * The qualitative comment on the progress of night cycles and alarm reports (if Automated Peritoneal Dialysis)
   */
  @attr("string") declare nightCycleProgress: string;

  /**
   * The temperature in °C
   */
  @attr("number") declare temperature: number;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.dp-cycle-declared-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      weight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.weight"
        )
      }),
      pocketVolume: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.pocket-volume"
        )
      }),
      systolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.systolic-pressure"
        )
      }),
      diastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.diastolic-pressure"
        )
      }),
      heartRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.heart-rate"
        )
      }),
      ultrafiltrationRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.ultrafiltration-rate"
        )
      }),
      urineOutput: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.urine-output"
        )
      }),
      temperature: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.dp-cycle-declared-journal-event.temperature"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "dpCycleDeclaredJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "dp-cycle-declared-journal-event": DpCycleDeclaredJournalEvent;
  }
}
